// @ts-nocheck
/**
 * ContactsScreen — the « Liste » page (/ParallelDialer), outside a call.
 *
 * Displays the view tabs (À appeler / Rappels / Archivés, ADR-031), the
 * search, the filters and their pills, saved views, the columns menu, the
 * prospect table (or kanban / grid), and the « Réglages d'appel » popover (⚙),
 * the single place for the call settings.
 *
 * W4 (B2-liste, docs/refonte-ui/audit-ui-ux.md):
 *   - states: skeleton while the queue's first read runs, error with
 *     « Réessayer », empty with one CTA, « filtered » with « Effacer les
 *     filtres » (DES-12);
 *   - bulk actions report failures, keep the failed rows selected and offer
 *     « Réessayer »; priority and status changes offer « Annuler » (DES-13);
 *   - no silent cap (T-d3): past the engine's 1000-contact read, the list is
 *     read through POST /api/entities/ProspectQueue/page
 *     (useProspectListBeyondCap). The engine's own queue read and polling
 *     (DialerDataContext, useDialerDerivedData, dialRules) are unchanged:
 *     Power Mode still dials from its 1000 most recent contacts, and the
 *     screen says so when older ones are shown.
 */

import { FILTER_STATUT_APPEL, FILTER_STATUT_LEAD, FILTER_TRI, optionsTraduites, filtreActif, valeursFiltre, passeFiltre } from '@/services/dialer/prospectFilters';
import { LIVE_CALL_STATUSES } from '@revcall/shared';
import { VFFilterBar, ListActionsSlot, ColumnsMenu } from '@/components/dialer/contacts/VFFilterBar';
import { ContactViewTabs } from '@/components/dialer/contacts/ContactViewTabs';
import { ListFilterPills } from '@/components/dialer/contacts/ListFilterPills';
import { PriorityDialog } from '@/components/dialer/contacts/PriorityDialog';
import { ContactsListState } from '@/components/dialer/contacts/ContactsListState';
import { CONTACT_VIEWS, EMPTY_LIST_FILTERS, readSavedViews, writeSavedViews, upsertSavedView, removeSavedView } from '@/services/dialer/savedListViews';
import { runBulk, isNotFound } from '@/services/dialer/bulkRun';
import { withProjectedLastCalls } from '@/services/dialer/lastCallProjection';
import { useProspectListBeyondCap, mergeBeyondCap, invalidateProspectListAllPages, ENGINE_QUEUE_LIMIT } from '@/hooks/useProspectListBeyondCap';
import { useQueryStateSnapshot, listLoadState } from '@/hooks/useQueryStateSnapshot';
import { useTabParam } from '@/hooks/useTabParam';
import { useSearchParams } from 'react-router-dom';
import { VFProspectAvatar } from '@/components/dialer/contacts/VFProspectAvatar';
import { getInitials } from '@/utils/initials';
import { visibleCustomColumns, toggleCustomColumn } from '@/services/dialer/customColumns';
import { VFTh } from '@/components/dialer/contacts/VFTh';
import { VFStatusPill, resolveStatusMetaFromIntent, STATUS_META } from '@/components/dialer/contacts/VFStatusPill';
import React, { Fragment, useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  RefreshCw,
  Settings,
  StickyNote,
  PhoneOff,
  AlertCircle,
  CheckCircle2,
  Voicemail,
  UserX,
  X,
  Mic,
  LayoutGrid,
  LayoutList,
  LayoutDashboard,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { useDialer } from '@/contexts/DialerContext';
import { hasMeetingBooked } from '@/utils/callUtils';
import LineTranscriptPreview from '@/components/dialer/call-panel/LineTranscriptPreview';
import { useTranslation } from 'react-i18next';
import KanbanView from '@/components/dialer/KanbanView';
import GridView from '@/components/dialer/GridView';
import BulkActions from '@/components/dialer/BulkActions';
import { dialIntent, reactivationTarget, effectiveLastCall, callOutcomeOf, isInterruptedCall } from '@/services/dialer/dialRules';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { formatDate } from '@/i18n/locale';
import { usePermissions } from '@/hooks/usePermissions';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';

// Statuts d'un appel encore « vivant ». La liste vient du paquet partagé ;
// `ai_analyzing` s'y ajoute, c'est une étape propre à cet écran et absente du
// vocabulaire de l'opérateur.
const CALL_LIVE_STATUSES = new Set<string>([...LIVE_CALL_STATUSES, 'ai_analyzing']);

const MAX_VISIBLE_TABS = 5;

// #4 — les trois vues, dans l'ordre du cycle. Un SEUL bouton les parcourt :
// PJ demandait « un seul toggle, comme le Night Mode », et #4 ajoute une
// troisieme vue — un interrupteur binaire ne pouvait plus convenir.
const VUE_CYCLE = ['list', 'kanban', 'grid'];
const VUE_SUIVANTE = (v) => VUE_CYCLE[(VUE_CYCLE.indexOf(v) + 1) % VUE_CYCLE.length];
const ITEMS_PER_PAGE = 100;

// K5 — a window of pages around the current one instead of one button per
// page (50 buttons for 5,000 contacts). Returns page indexes and 'gap'.
function pageWindow(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
  const keep = new Set([0, totalPages - 1, page - 1, page, page + 1]);
  if (page <= 2) [1, 2, 3].forEach((i) => keep.add(i));
  if (page >= totalPages - 3) [totalPages - 4, totalPages - 3, totalPages - 2].forEach((i) => keep.add(i));
  const pages = [...keep].filter((i) => i >= 0 && i < totalPages).sort((a, b) => a - b);
  return pages.flatMap((p, i) => (i > 0 && p - pages[i - 1] > 1 ? ['gap', p] : [p]));
}

function VFPagination({ page, totalPages, onChange }) {
  const { t } = useTranslation(['dialer']);
  const last = totalPages - 1;
  const navBtn = 'inline-flex items-center justify-center h-7 w-7 rounded-vf-sm text-vf-text-2 hover:bg-vf-surface-3 hover:text-vf-text-1 disabled:opacity-30 disabled:pointer-events-none transition-colors outline-none focus-visible:ring-1 focus-visible:ring-vf-accent';
  return (
    <nav className="flex items-center gap-0.5" aria-label={t('dialer:contacts.pagination.label')}>
      <button type="button" className={navBtn} onClick={() => onChange(0)} disabled={page === 0} aria-label={t('dialer:contacts.pagination.first')} title={t('dialer:contacts.pagination.first')}>
        <ChevronsLeft className="w-3.5 h-3.5" />
      </button>
      <button type="button" className={navBtn} onClick={() => onChange(page - 1)} disabled={page === 0} aria-label={t('dialer:contacts.pagination.previous')} title={t('dialer:contacts.pagination.previous')}>
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      {pageWindow(page, totalPages).map((p, i) => (p === 'gap' ? (
        <span key={`gap-${i}`} className="w-6 text-center text-xs text-vf-text-4 select-none" aria-hidden>…</span>
      ) : (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          aria-current={p === page ? 'page' : undefined}
          aria-label={t('dialer:contacts.pagination.page', { page: p + 1 })}
          className={`min-w-[28px] h-7 px-1.5 rounded-vf-sm text-xs font-medium vf-font-mono transition-colors outline-none focus-visible:ring-1 focus-visible:ring-vf-accent ${
            p === page
              ? 'bg-vf-accent-bg text-vf-accent border border-vf-accent-bd'
              : 'text-vf-text-2 hover:bg-vf-surface-3 hover:text-vf-text-1'
          }`}
        >
          {p + 1}
        </button>
      )))}
      <button type="button" className={navBtn} onClick={() => onChange(page + 1)} disabled={page === last} aria-label={t('dialer:contacts.pagination.next')} title={t('dialer:contacts.pagination.next')}>
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
      <button type="button" className={navBtn} onClick={() => onChange(last)} disabled={page === last} aria-label={t('dialer:contacts.pagination.last')} title={t('dialer:contacts.pagination.last')}>
        <ChevronsRight className="w-3.5 h-3.5" />
      </button>
    </nav>
  );
}

export default function ContactsScreen() {
  const d = useDialer();
  const { t } = useTranslation(['dialer', 'common']);
  const [ask, confirmDialog] = useConfirmDialog();
  const queryClient = useQueryClient();
  // A write made from the list refreshes the engine's queue AND the read past
  // its cap, which has its own key (REV-W4-F1).
  const invalidateList = () => {
    queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
    invalidateProspectListAllPages(queryClient);
  };
  const [isRefreshing, setIsRefreshing] = useState(false); // #47 — retour visuel du Refresh


  // ── Local state ────────────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [prospectSearch, setProspectSearch] = useState('');
  const [prospectFilters, setProspectFilters] = useState({ leadStatus: 'all', priority: 'all', lastCall: 'all', score: 'all', persona: 'all', city: 'all' });
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [renamingList, setRenamingList] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingList, setDeletingList] = useState(null);
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  // AUDIT-F23: the server refuses prospect deletion to callers (shared queue).
  const { isTenantAdmin } = usePermissions();
  // QA-W7-2: permissions_v2 may remove records.write from the role.
  const writeBlocked = usePermissionGuard()('records.write');
  const [showAllTabs, setShowAllTabs] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // « Réglages d'appel » is the single place for the call settings (W4,
  // B2-liste): Paramètres > Général links to /ParallelDialer?callSettings=open,
  // which opens the popover once, then drops the parameter.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('callSettings') !== 'open') return;
    setSettingsOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('callSettings');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [filterSections, setFilterSections] = useState({ status: false, lastCall: false, priority: false });
  // ADR-031 — 3 vues du tableau de contacts :
  //   'default'   → Power Mode candidates (fresh + no_answer + contacted)
  //   'callbacks' → uniquement les rappels demandés (callback_requested)
  //   'archived'  → RDV pris, pas intéressé, DNC
  // W4 (B2-liste): the view is a tab in plain sight, kept in `?tab=` so it
  // survives a reload and can be linked.
  const [contactView, setContactView] = useTabParam(CONTACT_VIEWS, 'default');
  const [reactivatingId, setReactivatingId] = useState(null);
  const [uploadingVoicemail, setUploadingVoicemail] = useState(false);

  // Debounced search
  useEffect(() => {
    const handle = setTimeout(() => setProspectSearch(searchInput), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // ── Queries ────────────────────────────────────────────────────────────────

  // Convenience aliases
  // ADR-024 — `prospectQueue` is the unfiltered tenant-wide queue. The screen
  // MUST consume `filteredProspectQueue` (scoped to selectedCustomerId) so
  // switching Customer in the sidebar actually scopes the contacts list.
  // Aliased to `prospectQueue` here so the rest of the screen reads naturally.
  const {
    callerData, filteredProspectQueue: engineQueue, allCalls, activeCalls,
    prospectsBySource, sources, latestCallByPhone: engineLatestCallByPhone, prospectCallMap,
    dialedPhonesThisSession,
    selectedSource, setSelectedSource, selectedProspects, setSelectedProspects,
    setSelectedProspect, powerMode, setPowerMode, kanbanEnabled, setKanbanEnabled,
    viewMode, setViewMode, dialerRunState, language,
    isProspectAtCallLimit, callerIdStats,
    selectedCallerId, setSelectedCallerId, twilioNumbers, activeCallerNumbers, callerIdRotation, setCallerIdRotation,
    ringTimeout, setRingTimeout, maxCallAttempts, setMaxCallAttempts, maxCallAttemptsPeriod, setMaxCallAttemptsPeriod,
    voicemailMode, setVoicemailMode, voicemailAudioUrl, setVoicemailAudioUrl,
    completeTaskOnDial, setCompleteTaskOnDial, soundEnabled, setSoundEnabled,
    darkMode, setDarkMode, parallelCallsCount, setParallelCallsCount,
    audioInputDevice, setAudioInputDevice, availableDevices,
    updateProspectQueueWithRetry, handleCallProspect,
    handleStartDialing, handleStopAll, handleSkipBatch,
    columnConfig, setColumnConfig,
  } = d;

  // ── T-d3: the list past the engine's cap ──────────────────────────────────
  // `prospectQueue` (above) is the engine's read, capped at ENGINE_QUEUE_LIMIT.
  // When it comes back full, the display reads every contact through the
  // paginated route and adds the ones the engine did not load. Their dial
  // state comes from their own projection (ENG-5), like the others.
  const beyondCap = useProspectListBeyondCap({
    engineQueueLength: (d.prospectQueue || []).length,
    callerId: callerData?.id,
    customerId: d.selectedCustomerId,
  });
  const { displayQueue, extraRows } = useMemo(() => {
    if (beyondCap.status !== 'ready' || !beyondCap.data) return { displayQueue: engineQueue, extraRows: [] };
    const { rows, extra } = mergeBeyondCap(engineQueue, beyondCap.data.items);
    return { displayQueue: rows, extraRows: extra };
  }, [engineQueue, beyondCap.status, beyondCap.data]);
  const displayLatestCallByPhone = useMemo(
    () => (extraRows.length ? withProjectedLastCalls(engineLatestCallByPhone || {}, extraRows) : engineLatestCallByPhone),
    [engineLatestCallByPhone, extraRows],
  );
  // Below, `prospectQueue` and `latestCallByPhone` are what the list SHOWS.
  const prospectQueue = displayQueue;
  const latestCallByPhone = displayLatestCallByPhone;

  // DES-12: the first read of the queue has its own state (skeleton, error),
  // read without becoming an observer of the engine's query.
  const queueKey = ['prospect-queue', callerData?.id, d.selectedCustomerId];
  const queueState = useQueryStateSnapshot(queueKey);
  const loadState = listLoadState(queueState, engineQueue.length);
  const retryQueue = () => { void queryClient.refetchQueries({ queryKey: queueKey, exact: true }); };


  // ── Correction #42 — colonnes visibles, memorisees PAR LISTE ─────────────
  // La cle inclut selectedSource : chaque liste garde sa propre configuration,
  // comme demande. 'name' n'est jamais masquable (c'est l'identite de la ligne).
  const COLUMN_LABELS = {
    status: t('dialer:table.status'),
    calls: t('dialer:table.calls'),
    sms: t('dialer:table.sms'),
    position: t('dialer:table.title'),
    company: t('dialer:table.company'),
    linkedin: 'LinkedIn',
    lastContact: t('dialer:contacts.columns.lastContact'),
    phone: t('common:field.phone'),
    city: t('dialer:table.city'),
  };
  const columnsStorageKey = `xdial-columns-${selectedSource || 'default'}`;
  const [hiddenCols, setHiddenCols] = useState/* eslint-disable-line */(() => {
    try {
      const raw = localStorage.getItem(`xdial-columns-${selectedSource || 'default'}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(columnsStorageKey);
      setHiddenCols(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch { setHiddenCols(new Set()); }
  }, [columnsStorageKey]);
  const showCol = (key) => !hiddenCols.has(key);

  // ── Correction #27 — ordre des colonnes, par utilisateur ET par liste ────
  // 🚨 Kevin, 2026-09-30 : HubSpot et « statut lead » retires. HubSpot n'est
  // pas propose aux clients, et le statut est deja la pastille tout a gauche
  // de la ligne : la colonne redisait la meme chose, en texte, et dans une
  // autre police puisqu'elle n'utilisait pas l'en-tete commun.
  const DEFAULT_COLUMN_ORDER = ['status', 'calls', 'sms', 'name', 'position', 'company', 'linkedin',
    'lastContact', 'phone', 'city'];
  // The score column was removed on 2026-09-23 (client request): it started
  // at 50 for everyone and steered nothing. A saved order may still list it;
  // it no longer has a header or a cell.
  const columnOrderKey = `xdial-column-order-${selectedSource || 'default'}`;
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
  const [draggedCol, setDraggedCol] = useState(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(columnOrderKey);
      const saved = raw ? JSON.parse(raw) : null;
      // Toute colonne absente de la sauvegarde (ajout produit) revient a la fin,
      // jamais perdue.
      setColumnOrder(saved
        ? [...saved.filter((k) => DEFAULT_COLUMN_ORDER.includes(k)),
           ...DEFAULT_COLUMN_ORDER.filter((k) => !saved.includes(k))]
        : DEFAULT_COLUMN_ORDER);
    } catch { setColumnOrder(DEFAULT_COLUMN_ORDER); }
  }, [columnOrderKey]);
  const hideColumnLabel = t('dialer:contacts.columnsPanel.hideColumn');
  // Call-limit tooltip: the period is a raw value ('day' / 'week' / 'month'),
  // it must reach the screen as a translated label.
  const periodLabel = t(
    maxCallAttemptsPeriod === 'day' ? 'dialer:contacts.settings.perDay'
      : maxCallAttemptsPeriod === 'month' ? 'dialer:contacts.settings.perMonth'
      : maxCallAttemptsPeriod === 'ever' ? 'dialer:contacts.settings.perEver'
      : 'dialer:contacts.settings.perWeek',
  );
  const COLUMN_HEADERS = {
    status: <VFTh onClick={() => handleSort('status')} sortBy={sortBy} sortDirection={sortDirection} col="status" onHide={() => toggleCol('status')}>{t('dialer:table.status')}</VFTh>,
    calls: <VFTh onClick={() => handleSort('calls')} sortBy={sortBy} sortDirection={sortDirection} col="calls" onHide={() => toggleCol('calls')} align="right">{t('dialer:table.calls')}</VFTh>,
    sms: <VFTh onClick={() => handleSort('sms')} sortBy={sortBy} sortDirection={sortDirection} col="sms" onHide={() => toggleCol('sms')} align="right">{t('dialer:table.sms')}</VFTh>,
    name: <VFTh onClick={() => handleSort('name')} sortBy={sortBy} sortDirection={sortDirection} col="name">{t('dialer:table.name')}</VFTh>,
    position: <VFTh onClick={() => handleSort('position')} sortBy={sortBy} sortDirection={sortDirection} col="position" onHide={() => toggleCol('position')}>{t('dialer:table.title')}</VFTh>,
    company: <VFTh onClick={() => handleSort('company')} sortBy={sortBy} sortDirection={sortDirection} col="company" onHide={() => toggleCol('company')}>{t('dialer:table.company')}</VFTh>,
    linkedin: <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}><span className="group/th inline-flex items-center gap-1">LinkedIn<button type="button" onClick={() => toggleCol('linkedin')} title={hideColumnLabel} aria-label={hideColumnLabel} className="opacity-0 group-hover/th:opacity-100 transition-opacity leading-none" style={{ color: 'var(--vf-text-4)', fontSize: 12 }}>×</button></span></th>,
    lastContact: <VFTh onClick={() => handleSort('lastContact')} sortBy={sortBy} sortDirection={sortDirection} col="lastContact" onHide={() => toggleCol('lastContact')}>{t('dialer:contacts.columns.lastContact')}</VFTh>,
    phone: <VFTh onClick={() => handleSort('phone')} sortBy={sortBy} sortDirection={sortDirection} col="phone" onHide={() => toggleCol('phone')}>{t('common:field.phone')}</VFTh>,
    city: <VFTh onClick={() => handleSort('city')} sortBy={sortBy} sortDirection={sortDirection} col="city" onHide={() => toggleCol('city')}>{t('dialer:table.city')}</VFTh>,
  };

  const moveColumn = (from, to) => {
    if (from === to) return;
    setColumnOrder((prev) => {
      const toIndex = prev.indexOf(to);
      const next = [...prev];
      const [moved] = next.splice(prev.indexOf(from), 1);
      next.splice(toIndex, 0, moved);
      try { localStorage.setItem(columnOrderKey, JSON.stringify(next)); } catch { /* stockage indisponible */ }
      return next;
    });
  };


  // ── Correction #89 — arret automatique apres X connectes ────────────────
  // Le dialer tournait jusqu'a epuisement de la liste. Un commercial qui veut
  // s'arreter apres 10 conversations devait surveiller lui-meme et cliquer
  // Stop au bon moment. 0 = desactive, comportement d'avant.
  // Saved with the other dialer settings (it was local to each browser).
  const stopAfterConnected = d.stopAfterConnected || 0;
  const persistStopAfter = (n) => d.setStopAfterConnected(n);
  // Connectes de la session : phones composes depuis le demarrage dont le
  // dernier resultat est un contact humain.
  const HUMAN_OUTCOMES_SESSION = ['contacted', 'connected', 'meeting_booked', 'callback_requested', 'not_interested', 'interested'];
  const connectedThisSession = useMemo(() => {
    const dialed = dialedPhonesThisSession instanceof Set
      ? [...dialedPhonesThisSession]
      : Array.isArray(dialedPhonesThisSession) ? dialedPhonesThisSession : [];
    return dialed.filter((ph) => HUMAN_OUTCOMES_SESSION.includes(latestCallByPhone[ph]?.call_outcome)).length;
  }, [dialedPhonesThisSession, latestCallByPhone]);

  useEffect(() => {
    if (!stopAfterConnected || stopAfterConnected <= 0) return;
    if (dialerRunState !== 'running') return;
    if (connectedThisSession < stopAfterConnected) return;
    handleStopAll?.();
    toast.success(t('dialer:contacts.toasts.stopGoalReached', { count: connectedThisSession }));
  }, [stopAfterConnected, connectedThisSession, dialerRunState, handleStopAll, t]);


  // #43 — valeurs reellement presentes dans la liste courante : un menu
  // de filtre ne doit proposer que ce qui existe.
  const personaOptions = useMemo(() => {
    const set = new Set(prospectQueue.map((p) => p.persona).filter(Boolean));
    return [...set].sort().map((v) => ({ value: v, label: v }));
  }, [prospectQueue]);
  const cityOptions = useMemo(() => {
    const set = new Set(prospectQueue.map((p) => p.prospect_city || p.city).filter(Boolean));
    return [...set].sort().map((v) => ({ value: v, label: v }));
  }, [prospectQueue]);
  const toggleCol = (key) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(columnsStorageKey, JSON.stringify([...next])); } catch { /* stockage indisponible */ }
      return next;
    });
  };

  // Skip batch visible uniquement tant qu'au moins un appel sonne (dialing/ringing).
  // Des qu'une ligne passe a 'connected_to_agent', skip n'a plus de sens — il faut
  // raccrocher manuellement la ligne connectee.
  const hasRingingCalls = (d.activeCalls || []).some(c => ['dialing', 'ringing'].includes(c.call_status));

  // Reset page when filters/source/search/view change (K9: the view was missing)
  useEffect(() => { setCurrentPage(0); }, [prospectSearch, prospectFilters, selectedSource, sortBy, sortDirection, contactView]);

  // callCountByPhone — fenêtré (jour/semaine/mois) → sert UNIQUEMENT à
  // isProspectAtCallLimit (limite légale de tentatives). N'est JAMAIS affiché
  // tel quel dans la table — l'agent verrait un compteur qui change selon
  // une période invisible.
  const callCountByPhone = useMemo(() => {
    const now = new Date();
    let periodStart = new Date(now);
    if (maxCallAttemptsPeriod === 'week') periodStart.setDate(periodStart.getDate() - 7);
    else if (maxCallAttemptsPeriod === 'month') periodStart.setMonth(periodStart.getMonth() - 1);
    else periodStart.setDate(periodStart.getDate() - 1);
    const map = {};
    allCalls.forEach(c => { const key = c.prospect_phone; if (!key) return; if (new Date(c.created_date) >= periodStart) map[key] = (map[key] || 0) + 1; });
    return map;
  }, [allCalls, maxCallAttempts, maxCallAttemptsPeriod]);

  // totalCallsByPhone — compteur HISTORIQUE total, sans fenêtre temporelle.
  // C'est ce que l'agent veut voir dans la colonne APPELS et dans le badge
  // "Sans réponse · N". "Combien de fois j'ai essayé ce mec en tout, avant
  // d'investir un Nème appel".
  const totalCallsByPhone = useMemo(() => {
    const map = {};
    allCalls.forEach(c => {
      const key = c.prospect_phone;
      if (!key || isInterruptedCall(c)) return;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [allCalls]);

  // Newest interrupted line per phone: the pill shows an icon while no real
  // call came after it.
  const interruptedAtByPhone = useMemo(() => {
    const map = {};
    allCalls.forEach(c => {
      const key = c.prospect_phone;
      if (!key || !isInterruptedCall(c)) return;
      const at = new Date(c.created_date || 0).getTime();
      if (!map[key] || at > map[key]) map[key] = at;
    });
    return map;
  }, [allCalls]);

  // smsCountByPhone — nombre de SMS échangés avec ce prospet, les deux sens
  // confondus, sans fenêtre temporelle. Même règle que la colonne APPELS : un
  // total historique, pas un compteur de la session.
  //
  // Le serveur renvoie des clés déjà normalisées sur les 9 derniers chiffres,
  // sans quoi un prospet importé en 06 12 34 56 78 ne serait jamais rapproché
  // du +33612345678 écrit par Twilio.
  const { data: smsCountByPhone = {} } = useQuery({
    queryKey: ['sms-counts'],
    queryFn: async () => {
      const { data } = await api.functions.invoke('getSmsCounts', {});
      return data.counts || {};
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });
  const smsCountFor = useCallback((phone) => {
    if (!phone) return 0;
    const digits = String(phone).replace(/[^\d]/g, '');
    const key = digits.length > 9 ? digits.slice(-9) : digits;
    return smsCountByPhone[key] || 0;
  }, [smsCountByPhone]);

  // ── Search highlight ───────────────────────────────────────────────────────
  const highlight = (text) => {
    if (!prospectSearch || !text) return text;
    const parts = String(text).split(new RegExp(`(${prospectSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === prospectSearch.toLowerCase()
        ? <mark key={i} className="bg-yellow-200/70 rounded px-0.5">{part}</mark>
        : part
    );
  };

  // ── Filtered & sorted prospects ────────────────────────────────────────────
  // ADR-031 — filtrage par vue (default / callbacks / archived). dialIntent()
  // catégorise chaque prospect, on n'affiche que ceux qui correspondent à la
  // vue active. Les placeholders sont toujours cachés.
  const filteredProspects = useMemo(() => {
    // Le filtre STATUT APPEL pilote ce que la vue "par defaut" doit montrer :
    // - 'all' (= "Prets a appeler") : exclut archives + callbacks (vue de travail).
    // - 'show_all' : aucune exclusion intent, on voit TOUT.
    // - autre valeur (ex 'not_interested') : pas d'exclusion intent non plus,
    //   on filtre purement sur l'outcome. Sinon "Pas interesse" ne remonte
    //   jamais de prospect car ils sont tous tagges 'archived' par dialIntent.
    // #5 — la selection est desormais un tableau. 'all' (Prets a appeler) et
    // 'show_all' (Tout afficher) restent des MODES exclusifs.
    const lastCallVals = valeursFiltre(prospectFilters.lastCall || 'all');
    const lastCallFilter = lastCallVals.length === 1 ? lastCallVals[0] : null;
    const skipIntentGuard = !(lastCallVals.length === 1 && lastCallVals[0] === 'all');
    return prospectQueue.filter(p => {
      if (p.status === 'placeholder') return false;
      const intent = dialIntent(p, latestCallByPhone[p.prospect_phone]);
      if (contactView === 'callbacks') {
        if (intent !== 'callback') return false;
      } else if (contactView === 'archived') {
        if (intent !== 'archived') return false;
      } else if (!skipIntentGuard && !prospectSearch) {
        // A search looks through every prospect, not only the ones ready to
        // call: a contacted prospect searched by name or number must show up
        // (2026-10-07).
        // Vue par defaut "Prets a appeler" : exclut callbacks, archives (DNC
        // inclus) ET contactes. 2026-05-13 — les "contactes" restent visibles
        // via le filtre STATUT APPEL "Contactes" mais ne polluent plus la pile
        // Power Mode (canCall les exclut aussi, dialRules.js).
        if (intent === 'callback' || intent === 'archived' || intent === 'contacted') return false;
        // 2026-09-23, client rule: once called, a prospect leaves "Ready to
        // call" for the list of its result ("No answer", "Message vocal",
        // iOS Filter...). Power Mode dials the list shown, so it never
        // redials them from here. A prospect on a live call stays visible.
        if (intent === 'no_answer' && !prospectCallMap[p.prospect_phone]) return false;
      }
      if (sources.length > 1) {
        if (selectedSource === 'Revcall Client Dashboard') {
          if (p.source && p.source !== 'Revcall Client Dashboard') return false;
        } else {
          if (p.source !== selectedSource) return false;
        }
      }
      // A number typed "06 99 71 68 23" must find "+33699716823" (2026-10-07):
      // phones compare on their digits, the national 0 or +33 left out.
      const searchDigits = String(prospectSearch || '').replace(/\D/g, '').replace(/^(?:0033|33|0)/, '');
      const phoneDigits = String(p.prospect_phone || '').replace(/\D/g, '').replace(/^(?:0033|33|0)/, '');
      if (prospectSearch && !(
        p.prospect_name?.toLowerCase().includes(prospectSearch.toLowerCase()) ||
        p.prospect_company?.toLowerCase().includes(prospectSearch.toLowerCase()) ||
        p.prospect_phone?.includes(prospectSearch) ||
        (searchDigits.length >= 4 && phoneDigits.includes(searchDigits)) ||
        p.prospect_title?.toLowerCase().includes(prospectSearch.toLowerCase())
      )) return false;
      if (!passeFiltre(prospectFilters.leadStatus, (v) => p.lead_status === v)) return false;
      // #43 — PERSONA et VILLE etaient des menus vides, sans effet : ils
      // allumaient le bouton "Effacer" sans jamais filtrer quoi que ce soit.
      // Ils filtrent desormais sur les champs reellement portes par la file.
      if (!passeFiltre(prospectFilters.persona, (v) => (p.persona || '') === v)) return false;
      if (!passeFiltre(prospectFilters.city, (v) => (p.prospect_city || p.city || '') === v)) return false;
      if (prospectFilters.priority !== 'all') {
        if (prospectFilters.priority === 'high' && (p.priority || 0) < 5) return false;
        if (prospectFilters.priority === 'medium' && ((p.priority || 0) < 1 || (p.priority || 0) > 4)) return false;
        if (prospectFilters.priority === 'low' && (p.priority || 0) > 0) return false;
      }
      // 'show_all' ne filtre pas sur l'outcome : on retourne TOUT (sauf placeholders).
      // #5 — les memes regles qu'avant, mais evaluees une fois par valeur cochee.
      if (lastCallFilter !== 'all' && lastCallFilter !== 'show_all') {
        const lastCall = effectiveLastCall(p, latestCallByPhone[p.prospect_phone]);
        // Older than the 500 calls the page loads: the prospect's own
        // last_call_status, stamped by the server at the call's end.
        const outcome = lastCall ? callOutcomeOf(lastCall) : (p.last_call_status || '');
        const accepte = (v) => {
          if (v === 'contacted') return ['contacted', 'connected', 'connected_to_agent'].includes(outcome);
          // Same grouping as the "No answer" badge: no human reached and no
          // message left.
          // An unassigned number has its own filter ("Numéro non attribué"): its
          // failed call must not also list it under "No answer" (2026-10-06).
          if (v === 'no_answer') {
            if (p.phone_unassigned === true || lastCall?.failure_reason === 'number_not_found') return false;
            return ['no_answer', 'not_reachable', 'busy', 'failed', 'canceled', 'voicemail'].includes(outcome);
          }
          if (v === 'voicemail') return outcome === 'left_voicemail';
          // #1 — sans cette branche, l'option du menu ne filtre rien : cette
          // cascade est le SEUL consommateur de FILTER_STATUT_APPEL.
          // Also the calls where the iPhone screening assistant was recognised
          // automatically (amd_result 'call_screening', 2026-10-07).
          if (v === 'ios_filter') return outcome === 'ios_filter' || lastCall?.amd_result === 'call_screening';
          if (v === 'callback_requested') return outcome === 'callback_requested';
          if (v === 'meeting_booked') return hasMeetingBooked(lastCall);
          if (v === 'not_interested') return outcome === 'not_interested';
          // Never called = no call at all, ever (2026-10-07). It read !lastCall,
          // but lastCall is looked up among the organisation's 500 most recent
          // calls only, and a re-queued prospect gets none either: prospects
          // called earlier came back as "never called". The server stamps
          // last_call_at on the prospect at every call's end.
          if (v === 'never_called') {
            if (p.last_call_at || p.last_call_status) return false;
            return !latestCallByPhone[p.prospect_phone];
          }
          if (v === 'phone_unassigned') return p.phone_unassigned === true;
          return true;
        };
        if (!passeFiltre(prospectFilters.lastCall, accepte)) return false;
      }
      return true;
    }).sort((a, b) => {
      // Sort active calls to top, sub-sorted by status priority: connected > human_detected > ringing > ai_analyzing > dialing
      const statusPriority = { connected_to_agent: 5, human_detected: 4, ringing: 3, ai_analyzing: 2, dialing: 1 };
      const aCall = prospectCallMap[a.prospect_phone];
      const bCall = prospectCallMap[b.prospect_phone];
      const aPri = aCall ? (statusPriority[aCall.call_status] || 0) : 0;
      const bPri = bCall ? (statusPriority[bCall.call_status] || 0) : 0;
      if (aPri !== bPri) return bPri - aPri;
      // Regle metier : prospects deja appeles dans la SESSION basculent en bas.
      const dialedSet = new Set(dialedPhonesThisSession || []);
      const aDialedSession = dialedSet.has(a.prospect_phone) ? 1 : 0;
      const bDialedSession = dialedSet.has(b.prospect_phone) ? 1 : 0;
      if (aDialedSession !== bDialedSession) return aDialedSession - bDialedSession;
      // ADR-031 — vue par défaut : tri par intent (fresh > no_answer > contacted)
      // pour que l'agent VOIE l'ordre dans lequel le Power Mode va dialer.
      // Vue callbacks / archived : pas de tri par intent (toutes les lignes
      // sont du même intent par définition).
      if (contactView === 'default' && !sortBy) {
        const intentOrder = { fresh: 0, no_answer: 1, contacted: 2, callback: 98, archived: 99 };
        const aIntent = dialIntent(a, latestCallByPhone[a.prospect_phone]);
        const bIntent = dialIntent(b, latestCallByPhone[b.prospect_phone]);
        const cmp = (intentOrder[aIntent] ?? 99) - (intentOrder[bIntent] ?? 99);
        if (cmp !== 0) return cmp;
        // 2026-05-13 — tri secondaire dans no_answer : moins de tentatives EN HAUT,
        // plus de tentatives EN BAS. Sinon "Sans reponse · 4" pouvait remonter
        // devant "Sans reponse · 1" via l'ordre de creation backend.
        if (aIntent === 'no_answer' && bIntent === 'no_answer') {
          const aTot = totalCallsByPhone[a.prospect_phone] || 0;
          const bTot = totalCallsByPhone[b.prospect_phone] || 0;
          if (aTot !== bTot) return aTot - bTot;
        }
      }
      if (!sortBy) return 0;
      // Chaque branche calcule l'ordre CROISSANT ; seul `desc` l'inverse.
      // Plusieurs branches calculaient deja un ordre decroissant, que `desc`
      // inversait une seconde fois (BUGS-OUVERTS.md, K6).
      let comparison = 0;
      if (sortBy === 'score') { comparison = (a.priority || 0) - (b.priority || 0); }
      // Les colonnes de comptage n'avaient aucune branche de tri : cliquer sur
      // l'en-tete APPELS changeait la fleche sans rien reordonner.
      else if (sortBy === 'calls') { comparison = (totalCallsByPhone[a.prospect_phone] || 0) - (totalCallsByPhone[b.prospect_phone] || 0); }
      else if (sortBy === 'sms') { comparison = smsCountFor(a.prospect_phone) - smsCountFor(b.prospect_phone); }
      else if (sortBy === 'position') { comparison = (a.prospect_title || '').toLowerCase().localeCompare((b.prospect_title || '').toLowerCase()); }
      else if (sortBy === 'notes') { comparison = (a.notes && a.notes.trim().length > 0 ? 1 : 0) - (b.notes && b.notes.trim().length > 0 ? 1 : 0); }
      else if (sortBy === 'company') { comparison = (a.prospect_company || '').toLowerCase().localeCompare((b.prospect_company || '').toLowerCase()); }
      else if (sortBy === 'name') { comparison = (a.prospect_name || '').toLowerCase().localeCompare((b.prospect_name || '').toLowerCase()); }
      else if (sortBy === 'phone') { comparison = (a.prospect_phone || '').localeCompare(b.prospect_phone || ''); }
      // K7 — l'en-tete VILLE n'avait aucune branche de tri.
      else if (sortBy === 'city') { comparison = (a.prospect_city || a.city || '').toLowerCase().localeCompare((b.prospect_city || b.city || '').toLowerCase()); }
      // #40 — le tri doit porter sur la MEME date que celle affichee dans la
      // colonne (prospect.last_call_at), sinon l'ordre semble aleatoire.
      // La date du ParallelCall reste en repli quand la file n'en porte pas.
      else if (sortBy === 'lastContact') {
        const dateOf = (p) => p.last_call_at || latestCallByPhone[p.prospect_phone]?.created_date || '';
        comparison = dateOf(a).localeCompare(dateOf(b));
      }
      else if (sortBy === 'status') {
        const order = { connected_to_agent: 100, human_detected: 90, ringing: 80, dialing: 70, ai_analyzing: 60, completed: 50, voicemail: 45, busy: 40, no_answer: 35, failed: 30, canceled: 20 };
        const rank = (p) => { const c = prospectCallMap[p.prospect_phone] || latestCallByPhone[p.prospect_phone]; return order[c?.call_status] ?? 0; };
        comparison = rank(a) - rank(b);
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [prospectQueue, sources, selectedSource, prospectSearch, prospectFilters, latestCallByPhone, prospectCallMap, sortBy, sortDirection, dialedPhonesThisSession, allCalls, contactView, totalCallsByPhone, smsCountFor]);

  // Power Mode dials exactly this list (see canCall scopeIds): publish the ids
  // shown, and lift the restriction when the screen goes away.
  const setDialScopeIds = d.setDialScopeIds;
  useEffect(() => {
    // A new Set every time re-rendered the whole dialer on each calls refetch
    // (2026-10-07): store it only when the ids actually changed.
    const ids = filteredProspects.map((p) => p.id);
    setDialScopeIds?.((prev) => (prev && prev.size === ids.length && ids.every((id) => prev.has(id)) ? prev : new Set(ids)));
  }, [filteredProspects, setDialScopeIds]);
  useEffect(() => () => setDialScopeIds?.(null), [setDialScopeIds]);

  // ADR-031 — compteurs des trois onglets de vue. W4 (B2-liste) : comptés
  // sur la liste active, comme les lignes que l'onglet affiche (un compteur
  // tous-listes annonçait 4 rappels et en montrait 2), sans filtre ni
  // recherche : c'est la taille de la vue, pas du résultat filtré.
  const viewCounts = useMemo(() => {
    const counts = { default: 0, callbacks: 0, archived: 0 };
    for (const p of prospectQueue) {
      if (p.status === 'placeholder') continue;
      if (sources.length > 1) {
        if (selectedSource === 'Revcall Client Dashboard') {
          if (p.source && p.source !== 'Revcall Client Dashboard') continue;
        } else if (p.source !== selectedSource) continue;
      }
      const intent = dialIntent(p, latestCallByPhone[p.prospect_phone]);
      if (intent === 'callback') counts.callbacks++;
      else if (intent === 'archived') counts.archived++;
      else if (intent === 'fresh' || (intent === 'no_answer' && prospectCallMap[p.prospect_phone])) counts.default++;
    }
    return counts;
  }, [prospectQueue, latestCallByPhone, sources, selectedSource, prospectCallMap]);
  // Every non-placeholder contact of the active list, whatever the view.
  const listSize = useMemo(() => prospectQueue.filter((p) => p.status !== 'placeholder'
    && (sources.length <= 1 || (selectedSource === 'Revcall Client Dashboard'
      ? (!p.source || p.source === 'Revcall Client Dashboard')
      : p.source === selectedSource))).length, [prospectQueue, sources, selectedSource]);

  // ── Active filters as pills, and saved views (W4, B2-liste) ──────────────
  const hasFilter = filtreActif(prospectFilters.lastCall) || filtreActif(prospectFilters.leadStatus) || !!searchInput.trim();
  const clearFilters = () => {
    setProspectFilters({ ...EMPTY_LIST_FILTERS });
    setSearchInput('');
    setProspectSearch('');
  };
  const callStatusOptions = optionsTraduites(FILTER_STATUT_APPEL, t);
  const leadStatusOptions = optionsTraduites(FILTER_STATUT_LEAD, t);
  const sortOptions = optionsTraduites(FILTER_TRI, t);
  const removeValue = (key, value) => {
    const rest = valeursFiltre(prospectFilters[key]).filter((v) => v !== value && v !== 'all');
    setProspectFilters({ ...prospectFilters, [key]: rest.length ? rest : 'all' });
  };
  const pills = [];
  if (searchInput.trim()) {
    const label = t('dialer:contacts.pills.search', { value: searchInput.trim() });
    pills.push({ id: 'search', label, removeLabel: t('dialer:contacts.pills.remove', { label }), onRemove: () => { setSearchInput(''); setProspectSearch(''); } });
  }
  for (const [key, title, options] of [['lastCall', t('dialer:filters.callStatus'), callStatusOptions], ['leadStatus', t('dialer:filters.leadStatus'), leadStatusOptions]]) {
    if (!filtreActif(prospectFilters[key])) continue;
    for (const v of valeursFiltre(prospectFilters[key]).filter((x) => x !== 'all')) {
      const label = t('dialer:contacts.pills.value', { name: title, value: options.find((o) => o.value === v)?.label ?? v });
      pills.push({ id: `${key}-${v}`, label, removeLabel: t('dialer:contacts.pills.remove', { label }), onRemove: () => removeValue(key, v) });
    }
  }
  if (sortBy) {
    const label = t('dialer:contacts.pills.value', {
      name: t('dialer:contacts.filters.sort.label'),
      value: sortOptions.find((o) => o.value === sortBy)?.label ?? COLUMN_LABELS[sortBy] ?? sortBy,
    });
    pills.push({ id: 'sort', label, removeLabel: t('dialer:contacts.pills.remove', { label }), onRemove: () => handleSort('default') });
  }

  const [savedViews, setSavedViews] = useState(() => readSavedViews());
  const persistSavedViews = (next) => { setSavedViews(next); writeSavedViews(next); };
  const sameView = (v) => v.view === contactView && v.search === searchInput.trim()
    && (v.sortBy || null) === (sortBy || null) && (!sortBy || v.sortDirection === sortDirection)
    && JSON.stringify(valeursFiltre(v.filters.lastCall)) === JSON.stringify(valeursFiltre(prospectFilters.lastCall))
    && JSON.stringify(valeursFiltre(v.filters.leadStatus)) === JSON.stringify(valeursFiltre(prospectFilters.leadStatus));
  const activeSavedViewId = savedViews.find(sameView)?.id ?? null;
  const applySavedView = (v) => {
    setContactView(v.view);
    setProspectFilters({ ...EMPTY_LIST_FILTERS, ...v.filters });
    setSearchInput(v.search);
    setProspectSearch(v.search);
    setSortBy(v.sortBy);
    setSortDirection(v.sortDirection);
  };
  const saveCurrentView = (name) => {
    persistSavedViews(upsertSavedView(savedViews, {
      name, view: contactView, filters: { ...EMPTY_LIST_FILTERS, ...prospectFilters },
      search: searchInput.trim(), sortBy: sortBy || null, sortDirection,
    }));
    toast.success(t('dialer:contacts.savedViews.saved', { name }));
  };
  // Reversible: removed at once, « Annuler » puts it back (DES-13).
  const removeSaved = (v) => {
    const before = savedViews;
    persistSavedViews(removeSavedView(savedViews, v.id));
    toast.success(t('dialer:contacts.savedViews.removed', { name: v.name }), {
      action: { label: t('common:actions.undo'), onClick: () => persistSavedViews(before) },
    });
  };

  // ── Sort handler ───────────────────────────────────────────────────────────
  const handleSort = (field) => {
    // 2026-09-15 — 'default' remet la liste dans l'ordre d'appel (tri par
    // dialIntent, ADR-031). Sans ce retour possible, l'agent qui touchait une
    // fois au tri ne pouvait plus retrouver l'ordre dans lequel le Power Mode
    // va composer. Cf. BUGS-OUVERTS.md (SORT-1).
    if (field === 'default') { setSortBy(null); setSortDirection('desc'); return; }
    if (sortBy === field) setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDirection('desc'); }
  };

  // ── Status change handlers ─────────────────────────────────────────────────
  const handleStatusChange = async (prospect, newStatus) => {
    try {
      await updateProspectQueueWithRetry(prospect.id, { status: newStatus });
      invalidateList();
      toast.success(t('dialer:contacts.toasts.statusUpdated'));
    } catch { toast.error(t('dialer:contacts.toasts.statusUpdateError')); invalidateList(); }
  };

  // ADR-032 — handleManualStatusChange n'écrit plus prospect.status.
  // Le badge affiché par dialIntent est dérivé du dernier call_outcome ;
  // muter prospect.status créait juste de la désynchro avec l'outcome réel
  // (bug observé : agent clique "Pas intéressé" → tableau affichait "Contacté").
  // On ne touche QUE le call_outcome du dernier ParallelCall.
  const handleManualStatusChange = async (prospect, newCallStatus) => {
    try {
      const outcomeMap = { no_answer: 'no_answer', busy: 'busy', voicemail: 'voicemail', connected: 'contacted', wrong_number: 'wrong_number' };
      const newOutcome = outcomeMap[newCallStatus] || 'not_reachable';
      const latestCall = latestCallByPhone[prospect.prospect_phone];
      if (latestCall?.id) {
        await api.entities.ParallelCall.update(latestCall.id, { call_outcome: newOutcome });
        queryClient.invalidateQueries({ queryKey: ['all-calls'] });
        queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      }
      // Si l'agent dit "pas de réponse" / "occupé" / "messagerie", il veut
      // pouvoir rappeler ce prospect dans la session courante.
      const isRetryable = newCallStatus === 'no_answer' || newCallStatus === 'busy' || newCallStatus === 'voicemail';
      if (isRetryable && d.clearDialedPhonesForRelance && prospect.prospect_phone) {
        d.clearDialedPhonesForRelance([prospect.prospect_phone]);
      }
      invalidateList();
      toast.success(t('dialer:contacts.toasts.statusUpdated'));
    } catch { toast.error(t('dialer:contacts.toasts.statusUpdateError')); }
  };

  const handleSingleDoNotCall = async (prospect) => {
    try {
      await api.entities.ProspectQueue.update(prospect.id, { status: 'do_not_call', do_not_call: true, do_not_call_at: new Date().toISOString() });
      invalidateList();
      toast.success(t('dialer:contacts.toasts.doNotCallMarked'));
    } catch { toast.error(t('dialer:contacts.toasts.doNotCallError')); }
  };

  // ── Bulk action handlers ───────────────────────────────────────────────────
  // ADR-032 — handleBulkCall ne mute plus prospect.status. Le bouton est
  // un signal d'intention ; le vrai lancement passe par le Power Mode qui
  // consomme la sélection via canCall / dialIntent.
  // This used to be a stub: it toasted "start calling", cleared the selection
  // and placed no call. One selected prospect is now called exactly like the
  // prospect card does: a single call, power mode off. Calling a multiple
  // selection is not implemented; the agent is told instead of silence.
  const handleBulkCall = async () => {
    if (selectedProspects.length !== 1) {
      toast.info(t('dialer:contacts.toasts.bulkCallOneOnly'));
      return;
    }
    const prospect = prospectQueue.find((p) => p.id === selectedProspects[0]);
    // A contact past the engine's cap (T-d3) is still called by hand, like
    // from its card.
    if (!prospect) return;
    setSelectedProspects([]);
    setPowerMode(false);
    await handleCallProspect(prospect);
  };

  // W4 (B2-liste): bulk updates used to swallow each error and toast « mis à
  // jour » regardless (BUGS-OUVERTS.md, LIST-BULK-1). Now: the failed rows
  // stay selected with « Réessayer » (DES-12), and a change that succeeded
  // offers « Annuler », which writes the previous values back (DES-13).
  // REV-W4-F16: the undo pass (and its Réessayer) runs later, from a toast,
  // while the agent may have selected other contacts: it must not replace
  // that selection with its own failures (`keepSelection`).
  const bulkUpdate = async (ids, patchFor, { successKey, partialKey, undoPatchFor, keepSelection = false }) => {
    const { ok, failed } = await runBulk(ids, (id) => api.entities.ProspectQueue.update(id, patchFor(id)));
    if (!keepSelection) setSelectedProspects(failed);
    invalidateList();
    if (failed.length === 0) {
      toast.success(t(successKey, { count: ok.length }), undoPatchFor ? {
        action: {
          label: t('common:actions.undo'),
          onClick: () => { void bulkUpdate(ok, undoPatchFor, { successKey: 'dialer:contacts.toasts.bulkUndone', partialKey: 'dialer:contacts.toasts.bulkUndoPartial', keepSelection: true }); },
        },
      } : undefined);
    } else {
      toast.error(t(partialKey, { count: failed.length, total: ids.length }), {
        action: { label: t('common:actions.retry'), onClick: () => { void bulkUpdate(failed, patchFor, { successKey, partialKey, undoPatchFor, keepSelection }); } },
      });
    }
  };
  const previousOf = (ids, field, fallback) => {
    const byId = new Map(prospectQueue.map((p) => [p.id, p]));
    return Object.fromEntries(ids.map((id) => [id, byId.get(id)?.[field] ?? fallback]));
  };

  // QW-d4: a Dialog instead of the browser's prompt().
  const handleBulkChangePriority = () => setPriorityDialogOpen(true);
  const applyBulkPriority = async (priority) => {
    setPriorityDialogOpen(false);
    const ids = [...selectedProspects];
    // REV-W4-F16: a contact without a priority gets « none » back (null),
    // not 0, when the change is undone.
    const before = previousOf(ids, 'priority', null);
    await bulkUpdate(ids, () => ({ priority }), {
      successKey: 'dialer:contacts.toasts.prioritiesUpdated',
      partialKey: 'dialer:contacts.toasts.priorityPartial',
      undoPatchFor: (id) => ({ priority: before[id] }),
    });
  };

  const handleBulkChangeStatus = async (newStatus) => {
    const ids = [...selectedProspects];
    const before = previousOf(ids, 'status', 'active');
    await bulkUpdate(ids, () => ({ status: newStatus }), {
      successKey: 'dialer:contacts.toasts.statusesUpdated',
      partialKey: 'dialer:contacts.toasts.statusPartial',
      undoPatchFor: (id) => ({ status: before[id] }),
    });
  };

  // Destructive: ConfirmDialog, and the list's name to type for more than one
  // contact (DES-13). 404 = already deleted (another tab): counted as done.
  const deleteProspects = async (ids) => {
    const { failed } = await runBulk(ids, (id) => api.entities.ProspectQueue.delete(id), isNotFound);
    setSelectedProspects(failed);
    invalidateList();
    if (failed.length > 0) {
      toast.error(t('dialer:contacts.toasts.deletePartial', { count: failed.length, total: ids.length }), {
        action: { label: t('common:actions.retry'), onClick: () => { void deleteProspects(failed); } },
      });
    } else {
      toast.success(t('dialer:contacts.toasts.prospectsDeleted'));
    }
  };
  const handleBulkDelete = async () => {
    const ids = [...selectedProspects];
    const listName = selectedSource || '';
    const ok = await ask({
      title: t('dialer:contacts.deleteDialog.title'),
      description: t('dialer:contacts.deleteDialog.body', { count: ids.length }),
      confirmLabel: t('common:actions.delete'),
      typedName: ids.length > 1 && listName ? listName : undefined,
    });
    if (ok) await deleteProspects(ids);
  };

  // Failed rows stay selected with a Réessayer, never silently dropped (DES-12).
  const blockProspects = async (ids) => {
    const failed = [];
    for (const id of ids) {
      try { await api.entities.ProspectQueue.update(id, { status: 'do_not_call', do_not_call: true, do_not_call_at: new Date().toISOString() }); }
      catch { failed.push(id); }
    }
    setSelectedProspects(failed);
    invalidateList();
    if (failed.length === 0) toast.success(t('dialer:contacts.toasts.bulkDoNotCallDone'));
    else toast.error(t('dialer:contacts.toasts.bulkDoNotCallPartial', { count: failed.length }), {
      action: { label: t('common:actions.retry'), onClick: () => { void blockProspects(failed); } },
    });
  };

  const handleBulkDoNotCall = async () => {
    const count = selectedProspects.length;
    if (!(await ask({ title: t('dialer:contacts.toasts.bulkDoNotCallConfirm', { count }), confirmLabel: t('dialer:contacts.toasts.bulkDoNotCallAction', { count }) }))) return;
    await blockProspects([...selectedProspects]);
  };


  const testMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioInputDevice ? { deviceId: { exact: audioInputDevice } } : true });
      toast.success(t('dialer:contacts.toasts.micWorks'));
      stream.getTracks().forEach((track) => track.stop());
    } catch (e) { toast.error(t('dialer:contacts.toasts.micError', { message: e.message })); }
  };

  // ── Tab overflow logic ─────────────────────────────────────────────────────
  const hasOverflow = sources.length > MAX_VISIBLE_TABS && !showAllTabs;
  const visibleSources = hasOverflow ? sources.slice(0, MAX_VISIBLE_TABS) : sources;
  const hiddenSources = hasOverflow ? sources.slice(MAX_VISIBLE_TABS) : [];
  const selectedInHidden = hiddenSources.includes(selectedSource);
  const finalVisible = selectedInHidden ? [...visibleSources.slice(0, MAX_VISIBLE_TABS - 1), selectedSource] : visibleSources;
  const finalHidden = selectedInHidden ? [visibleSources[MAX_VISIBLE_TABS - 1], ...hiddenSources.filter(s => s !== selectedSource)] : hiddenSources;

  // ADR-031 — bouton "Relancer les no-answer" supprimé.
  // Le Power Mode parcourt désormais fresh → no_answer → contacted dans l'ordre,
  // sans action manuelle. Pour les rappels demandés et les archivés, voir les
  // vues dédiées (toggles "Voir les rappels demandés" / "Voir les archivés").

  // Réactiver un prospect archivé (RDV pris / pas intéressé / DNC).
  // Option B (ADR-031) : si humain avait été joint, repart en tier 2 (contacted),
  // sinon en tier 0 (fresh). Le call_outcome du dernier appel est effacé pour
  // que canCall() le considère à nouveau dialable.
  const handleReactivate = useCallback(async (prospect) => {
    setReactivatingId(prospect.id);
    try {
      const lastCall = latestCallByPhone[prospect.prospect_phone];
      const target = reactivationTarget(lastCall);
      await api.entities.ProspectQueue.update(prospect.id, {
        status: target.prospectStatus,
        do_not_call: false,
      });
      // ADR-031 Option B : remplace ou efface l'outcome du dernier appel
      // pour que dialIntent recalcule correctement (contacted → tier 2,
      // null → fresh ou no_answer selon last_call_at).
      if (lastCall?.id) {
        // No longer swallowed: a failure here left the contact archived while
        // the toast said « réactivé » (LIST-BULK-1).
        await api.entities.ParallelCall.update(lastCall.id, { call_outcome: target.newOutcome });
      }
      if (d.clearDialedPhonesForRelance && prospect.prospect_phone) {
        d.clearDialedPhonesForRelance([prospect.prospect_phone]);
      }
      queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
      invalidateProspectListAllPages(queryClient);
      queryClient.invalidateQueries({ queryKey: ['all-calls'] });
      queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      toast.success(t('dialer:contacts.toasts.reactivated'));
    } catch {
      toast.error(t('dialer:contacts.toasts.reactivateError'));
    } finally {
      setReactivatingId(null);
    }
  }, [latestCallByPhone, d, queryClient, t]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(filteredProspects.length / ITEMS_PER_PAGE);
  // The list shrinks under the agent (calls leave "Ready to call", polling
  // every 5 s): never show a page past the end (K9).
  const page = Math.min(currentPage, Math.max(0, totalPages - 1));
  const paginatedProspects = filteredProspects.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  // ── Custom columns ─────────────────────────────────────────────────────────
  // Discover every custom_fields key present in the currently filtered
  // prospect set. These become toggleable extra columns (persisted via
  // columnConfig in DialerContext → localStorage).
  // #13 — CAUSE REELLE du deplacement du bouton de vue pendant une recherche.
  // Ces cles etaient derivees de `filteredProspects` : une recherche sans
  // resultat vidait la liste, donc les cles, donc le bouton « Colonnes »
  // disparaissait (rendu conditionnel) et toute la barre se decalait de 15 px
  // — mesure a l'ecran : bouton de vue x = 1011 -> 1026.
  // Les colonnes disponibles sont une propriete de la LISTE, pas du resultat
  // d'une recherche. On derive donc de prospectQueue. Effet de bord voulu :
  // l'agent ne perd plus son selecteur de colonnes au moment ou une recherche
  // ne donne rien.
  const availableCustomKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const p of prospectQueue) {
      if (p?.status === 'placeholder') continue;
      const cf = p?.custom_fields;
      if (cf && typeof cf === 'object') {
        for (const k of Object.keys(cf)) if (k) keys.add(k);
      }
    }
    return Array.from(keys).sort();
  }, [prospectQueue]);

  // La regle vit dans services/dialer/customColumns, avec ses tests : une
  // colonne du fichier importe ne s'affiche que si on l'a demandee.
  const visibleCustomKeys = useMemo(
    () => visibleCustomColumns(availableCustomKeys, columnConfig),
    [columnConfig, availableCustomKeys],
  );

  const basculerColonneImportee = (key: string) => {
    setColumnConfig(toggleCustomColumn(columnConfig, key));
  };

  const formatCustomLabel = (key: string) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // ──────────────────────────────────────────────────────────────────────────
  return (
    // DES-20: no entrance animation for the page or the rows; motion only
    // explains a change of state.
    <div className="space-y-2">
      {/* W4 (B2-liste) — the view decides what Power Mode dials: segmented
          tabs at the top of the list, with their counts and what they hold
          (it lived in the « ⋮ » menu, with a banner after the fact). */}
      <ContactViewTabs
        value={contactView}
        onChange={setContactView}
        counts={loadState === 'ready' ? viewCounts : null}
      />
      <ListFilterPills
        pills={pills}
        onClearAll={() => { clearFilters(); handleSort('default'); }}
        savedViews={savedViews}
        activeSavedViewId={activeSavedViewId}
        onApplySaved={applySavedView}
        onRemoveSaved={removeSaved}
        onSave={saveCurrentView}
      />

      {/* Correction #11 — la rangee d'actions (Outils ⋮ + Parametres ⚙️)
          occupait une ligne pleine largeur a elle seule au-dessus des
          filtres. Fusionnee ici : les filtres prennent la place
          disponible, les actions se collent a droite. Une ligne de
          chrome recuperee au profit de la liste d'appels. */}
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
        <VFFilterBar
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          prospectFilters={prospectFilters}
          setProspectFilters={setProspectFilters}
          filteredCount={filteredProspects.length}
          totalCount={prospectQueue.filter(p => p.source === selectedSource && p.status !== 'placeholder').length}
          viewMode={viewMode}
          setViewMode={setViewMode}
          setKanbanEnabled={setKanbanEnabled}
          availableCustomKeys={availableCustomKeys}
          visibleCustomKeys={visibleCustomKeys}
          onToggleCustomColumn={basculerColonneImportee}
          formatCustomLabel={formatCustomLabel}
          columnLabels={COLUMN_LABELS}
          showCol={showCol}
          onToggleCol={toggleCol}
          personaOptions={personaOptions}
          cityOptions={cityOptions}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* The refresh button (#47) was removed at the client's request on
              2026-09-23: the list already polls every 5 s. */}
          <ListActionsSlot slot="tools">
          <div className="flex items-center gap-2">
            {/* W4 (B2-liste) — the columns have their own menu; the « ⋮ »
                menu only holds the display (list / kanban / grid), under a
                name of its own (« Vue » named two different groups). */}
            <ColumnsMenu
              columnLabels={COLUMN_LABELS}
              showCol={showCol}
              onToggleCol={toggleCol}
              availableCustomKeys={availableCustomKeys}
              visibleCustomKeys={visibleCustomKeys}
              onToggleCustomColumn={basculerColonneImportee}
              formatCustomLabel={formatCustomLabel}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 flex-shrink-0"
                  style={{
                    background: 'var(--vf-surface-2)',
                    border: '1px solid var(--vf-border)',
                    color: 'var(--vf-text-2)',
                  }}
                  title={t('dialer:contacts.display.label')}
                  aria-label={t('dialer:contacts.display.label')}
                >
                  <MoreVertical className="w-4 h-4" aria-hidden />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-2" style={{ background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)' }}>
                <div role="group" aria-label={t('dialer:contacts.display.label')} className="space-y-0.5">
                  <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}>
                    {t('dialer:contacts.display.label')}
                  </div>
                  {[
                    { mode: 'list', Icon: LayoutList, labelKey: 'dialer:filters.view.list', kanban: false },
                    { mode: 'kanban', Icon: LayoutGrid, labelKey: 'dialer:filters.view.block', kanban: true },
                    // #4 — la 3e vue doit aussi etre atteignable depuis ce menu
                    { mode: 'grid', Icon: LayoutDashboard, labelKey: 'dialer:filters.view.grid', kanban: false },
                  ].map(({ mode, Icon, labelKey, kanban }) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={viewMode === mode}
                      onClick={() => { setKanbanEnabled(kanban); setViewMode(mode); }}
                      className="flex w-full items-center gap-2 px-2 h-9 rounded text-xs"
                      style={{
                        background: viewMode === mode ? 'var(--vf-accent-bg)' : 'transparent',
                        color: viewMode === mode ? 'var(--vf-accent)' : 'var(--vf-text-2)',
                      }}
                    >
                      <Icon className="w-3.5 h-3.5" aria-hidden />
                      {t(labelKey)}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            {/* W4 (B2-liste) — « Réglages d'appel » : the single place for
                the call settings (Paramètres > Général links here with
                ?callSettings=open). */}
            <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="bouton-reglages-appel"
                  aria-label={t('dialer:contacts.settings.title')}
                  title={t('dialer:contacts.settings.title')}
                  className={`h-8 w-8 p-0 flex-shrink-0 ${settingsOpen ? 'bg-[var(--vf-accent-bg)] border-[var(--vf-accent-bd)] text-[var(--vf-accent-text)]' : 'surface-1 border-border'}`}
                >
                  <Settings className="w-4 h-4" aria-hidden />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[480px] p-0" sideOffset={8}>
                <div className="bg-[var(--vf-accent-bg)] px-3 py-2 border-b border-border">
                  <h3 className="font-semibold text-sm text-foreground">{t('dialer:contacts.settings.title')}</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-border">
                  <div className="divide-y divide-border">
                    {/* Microphone */}
                    <div className="px-3 py-3">
                      <label className="text-xs font-semibold text-foreground/80 block mb-2">{t('dialer:contacts.settings.microphone')}</label>
                      <div className="flex gap-2">
                        <select value={audioInputDevice} onChange={(e) => setAudioInputDevice(e.target.value)} className="flex-1 min-w-0 bg-card border border-border text-foreground/80 text-sm rounded-md px-2 py-1.5 truncate">
                          {availableDevices.inputs.map((dev, i) => <option key={dev.deviceId} value={dev.deviceId}>{dev.label || t('dialer:contacts.settings.micFallback', { index: i + 1 })}</option>)}
                        </select>
                        <Button variant="outline" size="sm" onClick={testMicrophone} className="px-3"><Mic className="w-4 h-4 mr-1" />{t('dialer:contacts.settings.test')}</Button>
                      </div>
                    </div>
                    {/* #89 — arret apres X connectes */}
                  <div className="px-3 py-3">
                    <label className="text-xs font-semibold text-foreground/80 block mb-2">
                      {t('dialer:contacts.settings.stopAfter')}
                    </label>
                    <Select
                      value={String(stopAfterConnected)}
                      onValueChange={(v) => persistStopAfter(parseInt(v, 10))}
                    >
                      <SelectTrigger className="w-full h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">{t('dialer:status.disabled')}</SelectItem>
                        {[5, 10, 15, 20, 30, 50].map((n) => (
                          <SelectItem key={n} value={String(n)}>{t('dialer:contacts.settings.conversationsOption', { count: n })}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] mt-1.5" style={{ color: 'var(--vf-text-3)' }}>
                      {stopAfterConnected > 0
                        ? t('dialer:contacts.settings.sessionProgress', { current: connectedThisSession, target: stopAfterConnected })
                        : t('dialer:contacts.settings.runsToEndOfList')}
                    </p>
                  </div>

                  {/* Parallel calls */}
                    <div className="px-3 py-3">
                      <label className="text-xs font-semibold text-foreground/80 block mb-2">{t('dialer:contacts.settings.parallelCalls')}</label>
                      <Select value={parallelCallsCount.toString()} onValueChange={(v) => setParallelCallsCount(parseInt(v))}>
                        <SelectTrigger className="w-full h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{t('dialer:contacts.settings.callsOption', { count: n })}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="divide-y divide-border">
                    {/* Ring timeout */}
                    <div className="px-3 py-3">
                      <label className="text-xs font-semibold text-foreground/80 block mb-2">{t('dialer:contacts.settings.ringTimeout')}</label>
                      <Select value={String(ringTimeout)} onValueChange={(v) => setRingTimeout(Number(v))}>
                        <SelectTrigger className="w-full h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{[20,25,30,40,50,60].map(s => <SelectItem key={s} value={String(s)}>{t('dialer:contacts.settings.secondsOption', { count: s })}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {/* Sound + Complete task */}
                    <div className="px-3 py-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-foreground/80">{t('dialer:contacts.settings.soundNotifications')}</span>
                        <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-foreground/80">{t('dialer:contacts.settings.completeTasksOnDial')}</span>
                        <Switch checked={completeTaskOnDial} onCheckedChange={setCompleteTaskOnDial} />
                      </div>

                      {/* #12 -- mode Voicemail. L'etat existait deja dans
                          DialerContext ; il n'etait pas atteignable ici. */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-foreground/80">{t('dialer:contacts.settings.voicemailMode')}</span>
                        {/* voicemailMode is 'off' | 'recorded': `!!voicemailMode` was
                            always true, and the switch handed a boolean to a setter
                            expecting a mode, so any click turned the drop off. It
                            now drives the same toggle as the Voicemail drops page. */}
                        <Switch
                          checked={voicemailMode === 'recorded'}
                          onCheckedChange={(on) => setVoicemailMode(on ? 'recorded' : 'off')}
                        />
                      </div>

                      {/* #12 -- rotation des numeros d'emission. `callerIdRotation`
                          pilote deja le choix du numero (DialerContext, l.560+) :
                          'manual' garde le numero choisi, 'round-robin' alterne. */}
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-semibold text-foreground/80">
                          {t('dialer:contacts.settings.numberRotation')}
                        </span>
                        <Select
                          value={callerIdRotation || 'manual'}
                          onValueChange={setCallerIdRotation}
                        >
                          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual">{t('dialer:contacts.settings.rotationFixed')}</SelectItem>
                            <SelectItem value="round-robin">{t('dialer:contacts.settings.rotationAuto')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* #12 -- plafond d'appels par contact. ATTENTION : ce
                          reglage etait DEJA applique par canCall() via
                          `isAtCallLimit` -- un plafond agissait a l'aveugle,
                          sans que personne puisse le lire ni le changer. */}
                      {/* Label on its own line, the two selects sharing the width
                          below: side by side they overflowed the settings card. */}
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-foreground/80">
                          {t('dialer:contacts.settings.maxCallsPerContact')}
                        </span>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Select
                            // "Unlimited" is stored as 'unlimited', never 0: with 0
                            // every prospect counted as at its limit and Power Mode
                            // dialed nobody.
                            value={maxCallAttempts === 'unlimited' || !maxCallAttempts ? '0' : String(maxCallAttempts)}
                            onValueChange={(v) => setMaxCallAttempts(v === '0' ? 'unlimited' : Number(v))}
                          >
                            <SelectTrigger className="w-full h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">{t('dialer:contacts.settings.unlimited')}</SelectItem>
                              {[1, 2, 3, 4, 5, 8, 10].map((n) => (
                                <SelectItem key={n} value={String(n)}>{t('dialer:contacts.settings.callsOption', { count: n })}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={maxCallAttemptsPeriod || 'week'}
                            onValueChange={setMaxCallAttemptsPeriod}
                          >
                            <SelectTrigger className="w-full h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="day">{t('dialer:contacts.settings.perDay')}</SelectItem>
                              <SelectItem value="week">{t('dialer:contacts.settings.perWeek')}</SelectItem>
                              <SelectItem value="month">{t('dialer:contacts.settings.perMonth')}</SelectItem>
                              <SelectItem value="ever">{t('dialer:contacts.settings.perEver')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    {/* Caller ID */}
                    {(activeCallerNumbers.length > 0 || selectedCallerId) && (
                      <div className="px-3 py-3 space-y-2">
                        <label className="text-xs font-semibold text-foreground/80 block">{t('dialer:callBar.callerId')}</label>
                        <select value={selectedCallerId || ''} onChange={(e) => setSelectedCallerId(e.target.value)} className="w-full bg-card border border-border text-foreground/80 text-sm rounded-md px-2 py-1.5">
                          {selectedCallerId && !activeCallerNumbers.find(n => n.phoneNumber === selectedCallerId) && <option value={selectedCallerId}>{t('dialer:contacts.settings.callerIdVerified', { number: selectedCallerId })}</option>}
                          {activeCallerNumbers.map(num => {
                            const ns = callerIdStats[num.phoneNumber];
                            const cr = ns && ns.total > 0 ? Math.round((ns.connected / ns.total) * 100) : null;
                            const libelle = !ns
                              ? num.phoneNumber
                              : cr !== null
                                ? t('dialer:contacts.settings.callerIdOptionRate', { number: num.phoneNumber, count: ns.total, rate: cr })
                                : t('dialer:contacts.settings.callerIdOption', { number: num.phoneNumber, count: ns.total });
                            return <option key={num.sid} value={num.phoneNumber}>{libelle}</option>;
                          })}
                        </select>
                        {activeCallerNumbers.length > 1 && (
                          <select value={callerIdRotation} onChange={(e) => setCallerIdRotation(e.target.value)} className="w-full bg-card border border-border text-foreground/80 text-sm rounded-md px-2 py-1.5">
                            <option value="manual">{t('dialer:contacts.settings.rotationManual')}</option>
                            <option value="round-robin">{t('dialer:contacts.settings.rotationRoundRobin')}</option>
                            <option value="random">{t('dialer:contacts.settings.rotationRandom')}</option>
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          </ListActionsSlot>
        </div>
      </div>

      {/* #10 — les actions groupées, sur leur propre rangée, directement sous
          la ligne des onglets et des réglages. Elles étaient auparavant un
          calque `fixed bottom-6` posé par-dessus la barre d'appel
          (chevauchement mesuré : 632 x 32 px). Rendues dans le flux, elles ne
          peuvent plus rien recouvrir. Rangée propre et non partagée : les cinq
          actions font 728 px, elles ne tiennent pas à côté de la barre de
          filtres sans l'écraser — essayé, mesuré, écarté. */}
      {confirmDialog}
      <PriorityDialog
        open={priorityDialogOpen}
        count={selectedProspects.length}
        onOpenChange={setPriorityDialogOpen}
        onSubmit={(priority) => { void applyBulkPriority(priority); }}
      />
      {selectedProspects.length > 0 && (
        <div className="flex items-center">
          <BulkActions selectedCount={selectedProspects.length} onCall={handleBulkCall} onChangePriority={handleBulkChangePriority}
            onChangeStatus={handleBulkChangeStatus} onDelete={isTenantAdmin ? handleBulkDelete : undefined}
            onDoNotCall={handleBulkDoNotCall} onClear={() => setSelectedProspects([])} writeBlockedReason={writeBlocked} />
        </div>
      )}

      {/* T-d3 — never a silent cap. Past the engine's read, older contacts
          are read page by page and shown; Power Mode still dials from the
          ENGINE_QUEUE_LIMIT most recent ones, and the screen says so. */}
      {beyondCap.capped && loadState === 'ready' && (
        <p role="status" className="rounded-vf border px-3 py-2 text-[12px] flex flex-wrap items-center gap-x-2 gap-y-1"
          style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)', color: 'var(--vf-text-2)' }}>
          {beyondCap.status === 'loading' && t('dialer:contacts.cap.loading', { limit: ENGINE_QUEUE_LIMIT })}
          {beyondCap.status === 'error' && (
            <>
              <span>{t('dialer:contacts.cap.error', { limit: ENGINE_QUEUE_LIMIT })}</span>
              {beyondCap.error?.code && <span className="font-mono text-[11px]" style={{ color: 'var(--vf-text-3)' }}>{beyondCap.error.code}</span>}
              <Button type="button" variant="outline" size="sm" onClick={beyondCap.retry}>{t('common:actions.retry')}</Button>
            </>
          )}
          {beyondCap.status === 'ready' && (
            <>
              {!beyondCap.data.complete && beyondCap.data.total !== null && (
                <span className="tabular-nums font-medium" style={{ color: 'var(--vf-text-1)' }}>
                  {t('common:dataTable.partial', { shown: beyondCap.data.items.length, total: beyondCap.data.total })}
                </span>
              )}
              <span>
                {extraRows.length > 0
                  ? t('dialer:contacts.cap.dialNote', { count: extraRows.length, limit: ENGINE_QUEUE_LIMIT })
                  : t('dialer:contacts.cap.allLoaded', { limit: ENGINE_QUEUE_LIMIT })}
              </span>
            </>
          )}
        </p>
      )}

      {/* "Active Calls Monitor" retire — le suivi temps reel passe par les
          lignes surlignees du tableau (isBeingCalled + VFStatusPill). Un seul
          endroit pour voir les appels en cours = moins de confusion. */}

      {/* Queue view — DES-12 states first, then the rows in the chosen display. */}
      {loadState === 'loading' ? (
        <ContactsListState kind="loading" />
      ) : loadState === 'error' ? (
        <ContactsListState kind="error" code={queueState?.error?.code} onRetry={retryQueue} />
      ) : filteredProspects.length === 0 ? (
        hasFilter ? (
          <ContactsListState kind="filtered" onClear={clearFilters} />
        ) : contactView === 'default' && listSize > 0 ? (
          // Everyone in this list has been called: they wait under their
          // result, not « Importez une liste ».
          <ContactsListState kind="allCalled" onShowAll={() => setProspectFilters({ ...prospectFilters, lastCall: ['show_all'] })} />
        ) : (
          <ContactsListState
            kind="empty"
            view={contactView}
            onAction={contactView === 'default' ? () => d.setImportDialogOpen?.(true) : () => setContactView('default')}
          />
        )
      ) : viewMode === 'kanban' ? (
        <KanbanView prospects={filteredProspects} onProspectClick={d.setSelectedProspect} callMap={prospectCallMap} latestCallByPhone={latestCallByPhone} />
      ) : viewMode === 'grid' ? (
        /* #4 — 3e vue : grille animee, file d'attente en haut, termines en bas. */
        <GridView prospects={filteredProspects} onProspectClick={d.setSelectedProspect} callMap={prospectCallMap} latestCallByPhone={latestCallByPhone} />
      ) : (
        <div
          data-tour="prospect-table"
          className="rounded-vf-lg overflow-hidden"
          style={{
            // Correction #69 — la liste de contacts a appeler etait posee sur
            // --vf-surface-2 (#F4F5F8), un gris. Sur un ecran ou on lit des noms
            // toute la journee le fond doit etre blanc : le gris ecrase le
            // contraste du texte et fait passer la liste pour une zone inactive.
            background: 'var(--vf-bg)',
            border: '1px solid var(--vf-border)',
          }}
        >
          <div className="relative overflow-x-auto vf-scroll-visible">
            <table className="w-full">
              {/* Header VF — 8 colonnes conformes au screenshot "Page dialer dark mode.png"
                  checkbox · NAME · COMPANY · TITLE · CITY · STATUS · SCORE · CALLS */}
              <thead className="sticky top-0 z-10" style={{ background: 'var(--vf-surface-1)' }}>
                <tr style={{ borderBottom: '1px solid var(--vf-border)' }}>
                  <th className="px-4 py-2.5 text-left w-10">
                    <input type="checkbox" checked={selectedProspects.length === paginatedProspects.length && paginatedProspects.length > 0}
                      onChange={(e) => setSelectedProspects(e.target.checked ? paginatedProspects.map(p => p.id) : [])}
                      className="rc-checkbox"
                      aria-label={t('dialer:contacts.table.selectPage')}
                    />
                  </th>
                  {/* Correction #71 — ordre standardisé des colonnes.
                      Statut et nombre d'appels d'abord : c'est ce qu'on lit
                      pour décider qui appeler. Ville et Score, absents de la
                      liste des dix, sont conservés en fin de tableau. */}
                  {/* Correction #27 — en-tetes rendus depuis columnOrder :
                      l'ordre est une donnee, plus du JSX fige. Chaque en-tete
                      est deplacable au glisser-deposer. */}
                  {columnOrder.filter(showCol).map((colKey) => {
                    // Les handlers vont SUR le <th> : un <span> autour d'un <th>
                    // est ejecte par le parseur HTML et perd ses evenements.
                    const el = COLUMN_HEADERS[colKey];
                    const dp = {
                      draggable: true,
                      onDragStart: () => setDraggedCol(colKey),
                      onDragEnd: () => setDraggedCol(null),
                      onDragOver: (e) => e.preventDefault(),
                      onDrop: (e) => { e.preventDefault(); if (draggedCol) moveColumn(draggedCol, colKey); setDraggedCol(null); },
                      title: t('dialer:contacts.columnsPanel.reorderColumn'),
                      style: { cursor: 'grab', opacity: draggedCol === colKey ? 0.45 : 1 },
                    };
                    return (
                      <Fragment key={`th-${colKey}`}>
                        {React.cloneElement(el, typeof el.type === 'string' ? dp : { dragProps: dp })}
                      </Fragment>
                    );
                  })}
                  {visibleCustomKeys.map((k) => (
                    <th
                      key={`ch-${k}`}
                      className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider"
                      style={{ color: 'var(--vf-text-3)' }}
                      title={formatCustomLabel(k)}
                    >
                      {formatCustomLabel(k)}
                    </th>
                  ))}
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {paginatedProspects.map((prospect, index) => {
                  const activeCall = prospectCallMap[prospect.prospect_phone];
                  // 2026-05-14 — quand plusieurs ProspectQueue rows partagent le
                  // meme phone, seule la PREMIERE doit etre marquee "APPEL EN
                  // COURS" pour eviter d'afficher 12 lignes en sonnerie pour 3
                  // calls Twilio reels (bug du Redial 2026-05-14).
                  const isBeingCalled = !!activeCall && (
                    paginatedProspects.findIndex(p => p.prospect_phone === prospect.prospect_phone) === index
                  );
                  const callStatus = activeCall?.call_status;
                  const latestCall = latestCallByPhone[prospect.prospect_phone];
                  const callOutcome = latestCall?.call_outcome;
                  // totalCalls : compteur HISTORIQUE durable (ADR-031), affiché
                  // dans la cellule APPELS et dans le badge "Sans réponse · N".
                  const totalCalls = totalCallsByPhone[prospect.prospect_phone] || 0;
                  const intent = dialIntent(prospect, latestCall);
                  const interruptedAt = interruptedAtByPhone[prospect.prospect_phone];
                  const interrupted = !!interruptedAt && interruptedAt > new Date(latestCall?.created_date || 0).getTime();
                  const initials = getInitials(prospect.prospect_name);

                  /* 2026-09-15 — #39, le surlignement gris au survol, etait pose par
                     MUTATION IMPERATIVE du style inline dans onMouseEnter. Or le style
                     inline est reapplique a chaque rendu React, et cet ecran se re-rend
                     en permanence (cf. RENDER-1 : six effets executes 33 fois sur un seul
                     chargement) : le gris etait efface aussitot. Mesure avant correctif :
                     ligne survolee (`:hover` confirme par matches()) au fond
                     rgba(0,0,0,0), identique aux lignes voisines.
                     La couleur de survol passe au CSS, que React ne peut pas ecraser. Et
                     on cesse d'ecrire `background: transparent` en inline, qui l'emportait
                     sur toute regle :hover. Cf. BUGS-OUVERTS.md (#39). */
                  return (
                    <tr
                      key={prospect.id}
                      className="group cursor-pointer transition-colors motion-reduce:transition-none hover:bg-[var(--vf-surface-3)]"
                      style={{
                        borderBottom: '1px solid var(--vf-border)',
                        ...(isBeingCalled
                          ? {
                              background: ['connected_to_agent', 'human_detected'].includes(callStatus)
                                ? 'var(--vf-green-bg)'
                                : callStatus === 'ringing' ? 'var(--vf-orange-bg)' : 'var(--vf-yellow-bg)',
                            }
                          : {}),
                      }}
                    >
                      {/* Checkbox */}
                      <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedProspects.includes(prospect.id)}
                          onChange={(e) => setSelectedProspects(e.target.checked ? [...selectedProspects, prospect.id] : selectedProspects.filter(id => id !== prospect.id))}
                          className="rc-checkbox"
                          aria-label={t('dialer:contacts.table.selectRow', { name: prospect.prospect_name || prospect.prospect_phone || '' })}
                        />
                      </td>

                      {/* STATUS */}
                      {/* Correction #27 — cellules rendues dans le MEME ordre
                          que les en-tetes, depuis columnOrder. */}
                      {(() => {
                        const CELLS = {
                          status: (
                            <td className="px-3 py-2" onClick={() => d.setSelectedProspect(prospect)}>
                              <VFStatusPill
                                prospect={prospect}
                                intent={intent}
                                isBeingCalled={isBeingCalled}
                                callStatus={callStatus}
                                connectedAt={activeCall?.connected_at}
                                lastCall={latestCall}
                                totalCalls={totalCalls}
                                interrupted={interrupted}
                              />
                              {/* Aperçu transcript live (style Minari) — 10 premières
                                  secondes, uniquement pendant un appel en cours. */}
                              {isBeingCalled && activeCall?.id && CALL_LIVE_STATUSES.has(callStatus) && (
                                <LineTranscriptPreview callId={activeCall.id} />
                              )}
                            </td>
                          ),
                          calls: (
                            <td className="px-3 py-2 text-right" onClick={() => d.setSelectedProspect(prospect)}>
                              <span
                                className="vf-font-mono text-[13px] tabular-nums"
                                title={isProspectAtCallLimit(prospect.prospect_phone)
                                  ? t('dialer:contacts.table.limitReached', { max: maxCallAttempts, period: periodLabel })
                                  : t('dialer:contacts.table.totalCalls', { count: totalCalls })}
                                style={{
                                  color: totalCalls > 0 ? 'var(--vf-text-1)' : 'var(--vf-text-4)',
                                  textDecoration: isProspectAtCallLimit(prospect.prospect_phone) ? 'line-through' : 'none',
                                }}
                              >
                                {totalCalls}
                              </span>
                            </td>
                          ),
                          sms: (
                            <td className="px-3 py-2 text-right" onClick={() => d.setSelectedProspect(prospect)}>
                              <span
                                className="vf-font-mono text-[13px] tabular-nums"
                                title={t('dialer:contacts.table.totalSms', { count: smsCountFor(prospect.prospect_phone) })}
                                style={{ color: smsCountFor(prospect.prospect_phone) > 0 ? 'var(--vf-text-1)' : 'var(--vf-text-4)' }}
                              >
                                {smsCountFor(prospect.prospect_phone)}
                              </span>
                            </td>
                          ),
                          name: (
                          <td className="px-3 py-2" onClick={() => d.setSelectedProspect(prospect)}>
                            <div className="flex items-center gap-2.5 min-w-0">
                              <VFProspectAvatar initials={initials} isBeingCalled={isBeingCalled} callStatus={callStatus} />
                              {/* Le badge « Appel en cours » vivait ici EN PLUS de
                                  celui de la colonne Statut, à gauche : deux
                                  pastilles pour le même fait, sur la même ligne.
                                  Retiré le 2026-09-30, VFStatusPill reste la
                                  seule source. Le bouton raccrocher, lui, doit
                                  rester près du nom : c'est une action, pas un
                                  état. */}
                              <span className="text-[13px] font-medium truncate" style={{ color: 'var(--vf-text-1)' }}>
                                {highlight(prospect.prospect_name || '')}
                              </span>
                              {isBeingCalled && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); d.handleHangupSingleCall?.(activeCall.id); }}
                                  title={t('dialer:onCall.hangUp')}
                                  aria-label={t('dialer:onCall.hangUp')}
                                  className="inline-flex items-center justify-center shrink-0 rounded-full transition-colors"
                                  style={{ width: 20, height: 20, lineHeight: 1, color: 'var(--vf-red)', background: 'var(--vf-red-bg)' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--vf-red)'; e.currentTarget.style.color = 'white'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--vf-red-bg)'; e.currentTarget.style.color = 'var(--vf-red)'; }}
                                >
                                  <PhoneOff className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>
                          ),
                          position: (
                            <td className="px-3 py-2" onClick={() => d.setSelectedProspect(prospect)}>
                              <span
                                className="text-[13px] truncate block"
                                style={{ color: 'var(--vf-text-2)', maxWidth: '10rem' }}
                                title={prospect.prospect_title}
                              >
                                {highlight(prospect.prospect_title || '—')}
                              </span>
                            </td>
                          ),
                          company: (
                            <td className="px-3 py-2" onClick={() => d.setSelectedProspect(prospect)}>
                              <span
                                className="text-[13px] truncate block"
                                style={{ color: 'var(--vf-text-2)', maxWidth: '12rem' }}
                                title={prospect.prospect_company}
                              >
                                {highlight(prospect.prospect_company || '—')}
                              </span>
                            </td>
                          ),
                          linkedin: (
                            <td className="px-3 py-2" onClick={() => d.setSelectedProspect(prospect)}>
                              <div className="flex items-center">
                                    {prospect.linkedin_url ? (
                                      <a
                                        href={prospect.linkedin_url}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        onClick={(e) => e.stopPropagation()}
                                        title={t('dialer:contacts.table.linkedinOpen', { name: prospect.prospect_name || '' })}
                                        aria-label={t('dialer:contacts.table.linkedinOpen', { name: prospect.prospect_name || '' })}
                                        className="inline-flex items-center justify-center shrink-0 leading-none transition-opacity hover:opacity-80"
                                        style={{
                                          width: 16,
                                          height: 16,
                                          background: '#0A66C2',
                                          color: 'white',
                                          borderRadius: 3,
                                          fontSize: 10,
                                          fontWeight: 700,
                                          letterSpacing: '-0.02em',
                                        }}
                                      >
                                        in
                                      </a>
                                    ) : (
                                      <span
                                        aria-hidden="true"
                                        title={t('dialer:contacts.table.linkedinMissing')}
                                        className="inline-flex items-center justify-center shrink-0 leading-none pointer-events-none"
                                        style={{
                                          width: 16,
                                          height: 16,
                                          background: 'var(--vf-surface-3)',
                                          color: 'var(--vf-text-4)',
                                          borderRadius: 3,
                                          fontSize: 10,
                                          fontWeight: 700,
                                          letterSpacing: '-0.02em',
                                          opacity: 0.4,
                                        }}
                                      >
                                        in
                                      </span>
                                    )}
                              </div>
                            </td>
                          ),
                          lastContact: (
                            <td className="px-3 py-2" onClick={() => d.setSelectedProspect(prospect)}>
                              <span className="text-[13px] tabular-nums" style={{ color: 'var(--vf-text-2)' }}>
                                {prospect.last_call_at
                                  ? formatDate(prospect.last_call_at)
                                  : <span style={{ color: 'var(--vf-text-4)' }}>—</span>}
                              </span>
                            </td>
                          ),
                          phone: (
                            <td className="px-3 py-2" onClick={() => d.setSelectedProspect(prospect)}>
                              <span className="vf-font-mono text-[13px] tabular-nums whitespace-nowrap" style={{ color: 'var(--vf-text-2)' }}>
                                {prospect.prospect_phone || <span style={{ color: 'var(--vf-text-4)' }}>—</span>}
                              </span>
                            </td>
                          ),
                          city: (
                            <td className="px-3 py-2" onClick={() => d.setSelectedProspect(prospect)}>
                              <span className="text-[13px]" style={{ color: 'var(--vf-text-2)' }}>
                                {prospect.prospect_city || prospect.city || '—'}
                              </span>
                            </td>
                          ),
                        };
                        return columnOrder.filter(showCol).map((colKey) => (
                          <Fragment key={`td-${colKey}`}>{CELLS[colKey]}</Fragment>
                        ));
                      })()}

                      {/* Custom columns — render only visible keys, in stable order */}
                      {visibleCustomKeys.map((k) => {
                        const val = prospect?.custom_fields?.[k] ?? '';
                        return (
                          <td
                            key={`cc-${prospect.id}-${k}`}
                            className="px-3 py-2"
                            onClick={() => d.setSelectedProspect(prospect)}
                            title={val ? String(val) : ''}
                          >
                            <span className="text-[13px] truncate block max-w-[180px]" style={{ color: 'var(--vf-text-2)' }}>
                              {val || <span style={{ color: 'var(--vf-text-4)' }}>—</span>}
                            </span>
                          </td>
                        );
                      })}

                      {/* Actions menu (hover reveal) */}
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              aria-label={t('dialer:contacts.rowMenu.label', { name: prospect.prospect_name || prospect.prospect_phone || '' })}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-vf-sm opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                              style={{ color: 'var(--vf-text-3)' }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--vf-surface-3)'; e.currentTarget.style.color = 'var(--vf-text-1)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--vf-text-3)'; }}
                            >
                              <MoreVertical className="w-4 h-4" aria-hidden />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {contactView === 'archived' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleReactivate(prospect)}
                                  disabled={reactivatingId === prospect.id}
                                  className="text-green-600"
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  {t('dialer:contacts.rowMenu.reactivate')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuLabel>{t('dialer:contacts.rowMenu.changeStatus')}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleManualStatusChange(prospect, 'connected')}><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />{outcomeLabel('contacted', t)}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManualStatusChange(prospect, 'no_answer')}><PhoneOff className="w-4 h-4 mr-2 text-muted-foreground" />{outcomeLabel('no_answer', t)}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManualStatusChange(prospect, 'busy')}><AlertCircle className="w-4 h-4 mr-2 text-orange-500" />{outcomeLabel('busy', t)}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManualStatusChange(prospect, 'voicemail')}><Voicemail className="w-4 h-4 mr-2 text-muted-foreground" />{outcomeLabel('voicemail', t)}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManualStatusChange(prospect, 'wrong_number')}><UserX className="w-4 h-4 mr-2 text-red-500" />{outcomeLabel('wrong_number', t)}</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => d.setSelectedProspect(prospect)} className="gap-2"><StickyNote className="w-4 h-4" />{t('dialer:contacts.rowMenu.viewNotes')}</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleSingleDoNotCall(prospect)} className="text-orange-600"><PhoneOff className="w-4 h-4 mr-2" />{t('dialer:callBar.disableCalls')}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination — always show count, show page nav when >1 page */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-border">
            <span className="text-xs text-muted-foreground vf-font-mono">
              {totalPages > 1
                ? t('dialer:contacts.pagination.range', {
                    from: page * ITEMS_PER_PAGE + 1,
                    to: Math.min((page + 1) * ITEMS_PER_PAGE, filteredProspects.length),
                    total: filteredProspects.length,
                  })
                : t('dialer:contacts.pagination.count', { count: filteredProspects.length })
              }
            </span>
            {totalPages > 1 && (
              <VFPagination page={page} totalPages={totalPages} onChange={setCurrentPage} />
            )}
          </div>
        </div>
      )}

      {/* Bulk delete: ConfirmDialog via useConfirmDialog (handleBulkDelete). */}
      {/* Delete list dialog removed — now handled by XDialTopBar */}
    </div>
  );
}
